<template>
  <div class="terminal-view">
    <div class="terminal-header">
      <div class="row">
        <div class="col-9 mb-0 mb-xl-0">
          <h4 class="font-weight-bold">
            Terminal Logs
            <ToolTip
              content="View application logs in a terminal-style format. Real-time logs are displayed here."
            />
          </h4>
        </div>
      </div>
    </div>
    
    <div class="terminal-container">
      <div class="terminal-content" ref="terminalContent">
        <div v-for="(log, index) in logs" :key="index" class="log-entry" :class="log.logLevel">
          <span class="timestamp">{{ log.time }}</span>
          <span class="level">[{{ log.logLevel }}]</span>
          <span class="message">{{ log.message }}</span>
          <div v-if="log.data" class="log-details">
            <pre><code>{{ formatLogData(log.data) }}</code></pre>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import baseUrl from "@/shared/baseUrl";
import { useToast } from "vue-toastification";
import ToolTip from "@/components/tooltipComponent.vue";

export default {
  name: 'ActionLogTerminalView',
  components: {
    ToolTip,
  },
  props: {
    appId: {
      type: String,
      required: true
    },
    logs: {
      type: Array,
      required: true
    }
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  watch: {
    logs: {
      handler() {
        console.log('New logs received in terminal view:', this.logs.length);
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      },
      deep: true
    }
  },
  methods: {
    /**
     * Formats log data for display, handling both string and object data
     * @param {string|Object} data - The log data to format
     * @returns {string} Formatted data string
     */
    formatLogData(data) {
      try {
        if (typeof data === 'string') {
          console.log('Parsing string data as JSON');
          const parsed = JSON.parse(data);
          return JSON.stringify(parsed, null, 2);
        }
        console.log('Formatting object data');
        return JSON.stringify(data, null, 2);
      } catch (e) {
        console.warn('Failed to parse data, using original format:', e);
        return data;
      }
    },

    /**
     * Scrolls the terminal content to the bottom
     */
    scrollToBottom() {
      const container = this.$refs.terminalContent;
      if (container) {
        console.log('Scrolling terminal to bottom');
        container.scrollTop = container.scrollHeight;
      }
    }
  }
}
</script>

<style lang="css" scoped>
.terminal-view {
  background: #1e1e1e;
  border-radius: 6px;
  padding: 1rem;
  height: 100%;
}

.terminal-container {
  background: #000;
  border-radius: 4px;
  padding: 1rem;
  height: calc(100vh - 250px);
  overflow-y: auto;
}

.terminal-content {
  font-family: 'Courier New', Courier, monospace;
  color: #fff;
}

.log-entry {
  padding: 4px 0;
  border-bottom: 1px solid #333;
}

.timestamp {
  color: #888;
  margin-right: 10px;
}

.level {
  margin-right: 10px;
}

.message {
  color: #fff;
}

.log-entry.error .level {
  color: #ff4444;
}

.log-entry.info .level {
  color: #44ff44;
}

.log-entry.debug .level {
  color: #4444ff;
}

.log-entry.system .level {
  color: #ffff44;
}

.log-details {
  margin-left: 20px;
  margin-top: 5px;
  padding: 5px;
  background: #2a2a2a;
  border-radius: 3px;
}

.log-details pre {
  margin: 0;
  color: #ddd;
}

/* Scrollbar styling */
.terminal-content::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.terminal-content::-webkit-scrollbar-track {
  background: #1e1e1e;
}

.terminal-content::-webkit-scrollbar-thumb {
  background: #424242;
  border-radius: 4px;
}

.terminal-content::-webkit-scrollbar-thumb:hover {
  background: #4f4f4f;
}
</style> 